// Cross-route marketing navigation can fail to swap page content after prerender hydrate.
// reloadDocument on pathname changes matches the RecentCaptures / viewer workaround.

import React, { useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { isMarketingPrerenderPath } from '../utils/marketingPrerenderPaths';

function resolvePathname(to) {
  if (typeof to === 'string') return to.split(/[?#]/)[0] || '/';
  if (to && typeof to === 'object' && to.pathname) return to.pathname;
  return '';
}

function useMarketingReloadDocument(to) {
  const { pathname } = useLocation();
  const target = resolvePathname(to);
  return target !== pathname;
}

export function MarketingLink({ to, children, ...props }) {
  const reloadDocument = useMarketingReloadDocument(to);
  return (
    <Link to={to} reloadDocument={reloadDocument} {...props}>
      {children}
    </Link>
  );
}

export function MarketingNavLink({ to, children, ...props }) {
  const reloadDocument = useMarketingReloadDocument(to);
  return (
    <NavLink to={to} reloadDocument={reloadDocument} {...props}>
      {children}
    </NavLink>
  );
}

export function assignMarketingPath(path) {
  if (typeof window !== 'undefined') {
    window.location.assign(path);
  }
}

/**
 * Full-document navigation for any in-app link while on a prerendered page.
 * Catches account-menu Links (Studio / Settings / Help) that do not use MarketingLink.
 */
export function PrerenderHardNav() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined' || !isMarketingPrerenderPath(pathname)) {
      return undefined;
    }

    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === '/contact') return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return;
      }

      event.preventDefault();
      window.location.assign(`${url.pathname}${url.search}${url.hash}`);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [pathname]);

  return null;
}
